import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Paper,
  IconButton,
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useThemeStore } from '../store/themeStore';
import { StatusChip } from '../components/leads/StatusChip';
import { PriorityBadge } from '../components/leads/PriorityBadge';
import { LeadForm } from '../components/leads/LeadForm';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { Loading } from '../components/common/Loading';
import { LeadFormData } from '../types/lead.types';
import toast from 'react-hot-toast';

export const TestPage: React.FC = () => {
  const { mode, toggleTheme } = useThemeStore();
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const handleLeadSubmit = (values: LeadFormData) => {
    console.log('Lead submitted:', values);
    toast.success('Lead created successfully!');
    setShowLeadForm(false);
  };

  const handleConfirm = () => {
    toast.success('Action confirmed!');
    setShowConfirmDialog(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                MW Consultancy
              </Typography>
              <Typography variant="h5" color="text.secondary">
                Leads Management System - Test Page
              </Typography>
            </Box>
            <IconButton onClick={toggleTheme} color="inherit" size="large">
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Stack>
        </Paper>

        {/* Component Tests */}
        <Stack spacing={3}>
          {/* Status Chips Test */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Chips
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <StatusChip status="new" />
                <StatusChip status="contacted" />
                <StatusChip status="qualified" />
                <StatusChip status="proposal" />
                <StatusChip status="negotiation" />
                <StatusChip status="won" />
                <StatusChip status="lost" />
              </Stack>
            </CardContent>
          </Card>

          {/* Priority Badges Test */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Priority Badges
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <PriorityBadge priority="low" />
                <PriorityBadge priority="medium" />
                <PriorityBadge priority="high" />
                <PriorityBadge priority="urgent" />
              </Stack>
            </CardContent>
          </Card>

          {/* Interactive Components Test */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interactive Components
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={() => setShowLeadForm(true)}
                >
                  Open Lead Form
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setShowConfirmDialog(true)}
                >
                  Show Confirm Dialog
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={() => {
                    setShowLoading(true);
                    setTimeout(() => setShowLoading(false), 2000);
                  }}
                >
                  Test Loading (2s)
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => toast.success('Toast notification works!')}
                >
                  Test Toast
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Theme Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Theme
              </Typography>
              <Typography variant="body1">
                Mode: <strong>{mode === 'dark' ? 'Dark' : 'Light'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click the theme toggle button in the header to switch between dark and light modes.
                Your preference is saved in localStorage.
              </Typography>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  ✅ Configuration: Loaded
                </Typography>
                <Typography variant="body2">
                  ✅ State Management: Active (Zustand)
                </Typography>
                <Typography variant="body2">
                  ✅ Theme System: Working
                </Typography>
                <Typography variant="body2">
                  ✅ Components: Rendered
                </Typography>
                <Typography variant="body2">
                  ✅ Material-UI v9: Configured
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Dialogs */}
        <LeadForm
          open={showLeadForm}
          onClose={() => setShowLeadForm(false)}
          onSubmit={handleLeadSubmit}
          title="Create New Lead"
        />

        <ConfirmDialog
          open={showConfirmDialog}
          title="Confirm Action"
          message="Are you sure you want to proceed with this action?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
          confirmColor="primary"
        />

        {/* Loading Overlay */}
        {showLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Paper sx={{ p: 4 }}>
              <Loading message="Testing loading component..." />
            </Paper>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            © 2026 MW Consultancy. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};